import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import { RowNode } from "ag-grid-community"
import { buildLaborRateMatchContextParams } from "../../../rate-tables/utils"
import { useRowData } from "../../../common/ag-grid-ts-utils"
import { selectLaborRatesById } from "../../../rate-tables/selectors"
import { AppDispatch, TkTimeType } from "../../../common/types"
import { colorAttentionBlue50, fontSizeS, fontSizeXs, spacingXs } from "@rhumbix/rmbx_design_system_web"
import { zIndex } from "../../../common/styles"
import { useState, useEffect } from "react"
import { referenceableDataSelector } from "../../../selectors"
import { checkLaborRateMatch } from "../../../rate-tables/actions"
import { tEntitySelection } from "../../../cached-data/types"

/**
 * Props for the LaborRateMatchBadge component.
 */
interface LaborRateMatchBadgeProps {
    /** The AG Grid row node - use node.data to get current row data */
    node: RowNode
    /** The time type to check for rate matches: ST, OT, or DT */
    timeType: Exclude<TkTimeType, "TT">
}

/**
 * Badge component that displays predicted or applied labor rate information.
 * Shows a badge with a tooltip containing the Rate Table and Labor Rate names when a match is found.
 */
const LaborRateMatchBadge: React.FC<LaborRateMatchBadgeProps> = ({ node, timeType }) => {
    const dispatch = useDispatch() as AppDispatch
    const cachedLaborRatesById = useSelector(selectLaborRatesById)
    const referenceableData: tEntitySelection = useSelector(referenceableDataSelector)
    const [matchedLaborRateId, setMatchedLaborRateId] = useState<number | null>(null)
    // Trigger re-renders when AG-Grid updates the node data.
    const rowData = useRowData(node)
    // Row data changes a lot, so we'll keep track of just the fields we're interested in to better detect
    // when matching needs to be re-run.
    const { employeeId, projectId, costCodeId, modifiersString, entryDate } = buildLaborRateMatchContextParams(
        rowData,
        referenceableData
    )

    // When it looks like relevant row data has changed, re-run matching.
    useEffect(
        () => {
            const runMatching = async () => {
                const matchId = await dispatch(
                    checkLaborRateMatch({ employeeId, projectId, costCodeId, modifiersString, entryDate }, timeType)
                )
                setMatchedLaborRateId(matchId)
            }
            runMatching()
        },
        // These are all primitive values so that the effect only runs when they change.
        // Avoid tracking objects that may get rebuilt every render here.
        [employeeId, projectId, costCodeId, modifiersString, entryDate, timeType]
    )

    if (!matchedLaborRateId) {
        return null
    }

    // Look up cached labor rate to get enriched info (rate_table.name)
    const cachedRate = cachedLaborRatesById[matchedLaborRateId]
    if (!cachedRate) {
        return null
    }

    const rateName = cachedRate?.name ?? "Rate applied"
    const rateTableName = cachedRate?.rate_table?.name ?? ""
    const tooltipId = `labor-rate-match-${rowData?.id ?? "unknown"}-${timeType}`

    return (
        <>
            <MatchBadge data-tooltip-id={tooltipId} />
            <Portal container={document.body}>
                <ReactTooltip
                    id={tooltipId}
                    place="top"
                    float={true}
                    variant="dark"
                    style={{ zIndex: zIndex.Tooltip }}
                >
                    <TooltipContent>
                        <RateName>{rateName}</RateName>
                        {rateTableName ? <RateTableName>{rateTableName}</RateTableName> : null}
                        <RateStatus>Pending</RateStatus>
                    </TooltipContent>
                </ReactTooltip>
            </Portal>
        </>
    )
}

const MatchBadge = styled.div`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: ${colorAttentionBlue50};
    flex-shrink: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: ${fontSizeXs};
    font-weight: 400;

    &::before {
        content: "$";
    }
`

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
`

const RateStatus = styled.span`
    font-size: ${fontSizeXs};
    font-style: italic;
    margin-top: ${spacingXs};
`

const RateTableName = styled.span`
    font-size: ${fontSizeXs};
`

const RateName = styled.span`
    font-size: ${fontSizeS};
`

export default LaborRateMatchBadge
